import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export function CoachSettings() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const authUser = useAuthStore((s) => s.user);
  const [tenantName, setTenantName] = useState('');
  const [userName, setUserName] = useState('');
  const [initialTenantName, setInitialTenantName] = useState('');
  const [initialUserName, setInitialUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abandoned = false;
    api.coach.me()
      .then((me) => {
        if (abandoned) return;
        if (!me.isCoach) {
          navigate(routes.coach.welcome(), { replace: true });
          return;
        }
        setTenantName(me.tenant.name);
        setUserName(me.userName);
        setInitialTenantName(me.tenant.name);
        setInitialUserName(me.userName);
      })
      .catch((err) => {
        if (!abandoned) setError(err instanceof Error ? err.message : 'Fehler beim Laden.');
      })
      .finally(() => {
        if (!abandoned) setLoading(false);
      });
    return () => { abandoned = true; };
  }, [navigate]);

  const isDirty = tenantName.trim() !== initialTenantName || userName.trim() !== initialUserName;

  async function handleSave() {
    if (!tenantName.trim() || !userName.trim()) {
      toast.error('Anzeigename und Praxisname dürfen nicht leer sein.');
      return;
    }
    setSaving(true);
    try {
      await api.coach.updateMe({
        tenantName: tenantName.trim() !== initialTenantName ? tenantName.trim() : undefined,
        userName: userName.trim() !== initialUserName ? userName.trim() : undefined,
      });
      setInitialTenantName(tenantName.trim());
      setInitialUserName(userName.trim());
      // Update local auth store so the sidebar reflects the new name immediately
      if (authUser && userName.trim() !== authUser.name) {
        setUser({ ...authUser, name: userName.trim() });
      }
      toast.success('Gespeichert.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell pageTitle="Einstellungen">
      <div className="px-4 md:px-8 py-6 max-w-2xl mx-auto w-full">
        <h1 className="text-2xl font-semibold mb-6">Einstellungen</h1>

        {error && (
          <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Lade…</p>
        ) : (
          <div className="space-y-6">
            <section className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Profil</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  So erscheinst du in der App und in zukünftigen Berichten.
                </p>
              </div>

              <label className="block">
                <span className="text-sm font-medium">Anzeigename</span>
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="z.B. Anna Müller"
                  className="mt-1"
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  Erscheint in der Sidebar und später als Absender in Berichten.
                </span>
              </label>

              <label className="block">
                <span className="text-sm font-medium">Praxisname</span>
                <Input
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder="z.B. Coaching Praxis Müller"
                  className="mt-1"
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  Der Name deiner Praxis oder deines Coaching-Angebots.
                </span>
              </label>

              <div className="flex justify-end pt-2">
                <Button onClick={handleSave} disabled={!isDirty || saving}>
                  {saving ? 'Speichere…' : 'Speichern'}
                </Button>
              </div>
            </section>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
